import React, { useEffect, useMemo, useState } from "react";
import { Button } from "reactstrap";
import ReactMarkdown from "react-markdown";
import {
	buildDualLensLineSegments,
	prepareMarkdownForBlocks,
} from "./dualLensMarkdownDiff";
import {
	buildDualLensSpine,
	countHumanDiffRows,
	countScrollAiDiffRows,
	countScrollIdleDiffRows,
} from "./dualLensSpine";
import { dualLensMarkdownComponents } from "./dualLensMarkdownComponents";
import { formatFetchMeta, getFetchMeta } from "./dualLensMeta";
import { useSyncedTripleBlockScroll } from "./useSyncedTripleBlockScroll";

const AI_CONFIG = {
	title: "AI View",
	icon: "fa-robot",
	iconClass: "dual-lens__panel-icon--ai",
	subtitle: "Browser without JavaScript",
	description:
		"Chromium with JavaScript disabled - only static HTML is available, so dynamic content does not load.",
};

const HUMAN_IDLE_CONFIG = {
	subtitle: "Browser after page load",
	description:
		"What a visitor usually sees once the page finishes loading in a real browser.",
};

const SHOW_COMPARISON_MODE = false;

const HUMAN_SCROLL_CONFIG = {
	subtitle: "Browser after scrolling",
	description:
		"What appears after scrolling the page - includes lazy-loaded sections many users trigger.",
};

function resolveDiffLines(humanAddedLines, scrollAddedLines, diffProfile) {
	if (diffProfile === "scroll") {
		return { humanAddedLines: [], scrollAddedLines };
	}
	if (diffProfile === "idle") {
		return { humanAddedLines, scrollAddedLines: [] };
	}
	return { humanAddedLines, scrollAddedLines };
}

function hasDiffForProfile(humanAddedLines, scrollAddedLines, diffProfile) {
	if (diffProfile === "scroll") return scrollAddedLines.length > 0;
	if (diffProfile === "idle") return humanAddedLines.length > 0;
	return humanAddedLines.length > 0 || scrollAddedLines.length > 0;
}

const markdownComponentsWithNoImg = {
	...dualLensMarkdownComponents,
	img: () => null,
};

function getMarkdown(content, key) {
	return content?.markdown?.[key]?.trim() ?? "";
}

function AddedBadge({ count, variant = "ai" }) {
	if (count <= 0) return null;
	const label =
		variant === "scroll"
			? `+ ${count} section${count !== 1 ? "s" : ""} vs without interaction`
			: `+ ${count} section${count !== 1 ? "s" : ""} vs AI View`;
	return (
		<span
			className={`dual-lens__panel-badge${
				variant === "scroll" ? " dual-lens__panel-badge--scroll" : ""
			}`}
		>
			{label}
		</span>
	);
}

function MarkdownContent({ content, humanAddedLines, scrollAddedLines = [], showDiff }) {
	if (!showDiff) {
		return (
			<ReactMarkdown components={markdownComponentsWithNoImg}>{content}</ReactMarkdown>
		);
	}

	const hasHighlight = humanAddedLines.length > 0 || scrollAddedLines.length > 0;

	if (hasHighlight) {
		const segments = buildDualLensLineSegments(
			content,
			new Set(humanAddedLines),
			new Set(scrollAddedLines)
		);
		return (
			<>
				{segments.map((segment, index) =>
					segment.kind === "added" ? (
						<div
							key={index}
							className={`dual-lens__segment-added dual-lens__segment-added--${
								segment.variant === "human" ? "human" : "scroll"
							}`}
							title={
								segment.variant === "human"
									? "Not in AI View (JS / Human capture)"
									: "Only in scroll capture (beyond idle)"
							}
						>
							<ReactMarkdown components={markdownComponentsWithNoImg}>
								{segment.content}
							</ReactMarkdown>
						</div>
					) : (
						<div key={index} className="dual-lens__segment">
							<ReactMarkdown components={markdownComponentsWithNoImg}>
								{segment.content}
							</ReactMarkdown>
						</div>
					)
				)}
			</>
		);
	}

	return <ReactMarkdown components={markdownComponentsWithNoImg}>{content}</ReactMarkdown>;
}

function SpineRow({ rowIndex, cell, showDiff, diffProfile, isHovered, onRowHover }) {
	const hoverHandlers = {
		onMouseEnter: () => onRowHover(rowIndex),
		onMouseLeave: () => onRowHover(null),
	};

	if (cell.type === "placeholder") {
		return (
			<div
				className={`dual-lens__spine-row dual-lens__spine-placeholder dual-lens__spine-placeholder--${cell.variant}${
					isHovered ? " dual-lens__spine-row--hovered" : ""
				}`}
				data-dl-row-index={rowIndex}
				data-dl-placeholder="true"
				aria-label={cell.variant === "missing-ai" ? "Not in AI View" : "Not in Human View"}
				{...hoverHandlers}
			>
				<span className="dual-lens__spine-placeholder-label">{cell.label}</span>
			</div>
		);
	}

	const hasHighlight =
		showDiff &&
		hasDiffForProfile(cell.humanAddedLines, cell.scrollAddedLines, diffProfile);

	const diffLines = resolveDiffLines(
		cell.humanAddedLines,
		cell.scrollAddedLines,
		diffProfile
	);

	return (
		<div
			className={`dual-lens__spine-row${hasHighlight ? " dual-lens__spine-row--has-diff" : ""}${
				isHovered ? " dual-lens__spine-row--hovered" : ""
			}`}
			data-dl-row-index={rowIndex}
			data-dl-placeholder="false"
			{...hoverHandlers}
		>
			<div className="dual-lens__row-markdown">
				<MarkdownContent
					content={cell.content}
					humanAddedLines={diffLines.humanAddedLines}
					scrollAddedLines={diffLines.scrollAddedLines}
					showDiff={showDiff}
				/>
			</div>
		</div>
	);
}

function SpineScrollPanel({
	scrollRef,
	columnRows,
	hoveredRowIndex,
	onRowHover,
	showDiff,
	diffProfile,
	emptyMessage,
	hidden = false,
	stackedTab = false,
}) {
	const bodyClass = stackedTab
		? `dual-lens__panel-body dual-lens__panel-body-tab${
				hidden ? " dual-lens__panel-body-tab--hidden" : ""
		  }`
		: "dual-lens__panel-body";

	return (
		<div ref={scrollRef} className={bodyClass}>
			{columnRows.length > 0 ? (
				<article className="dual-lens__spine-article">
					{columnRows.map(({ rowIndex, cell }) => (
						<SpineRow
							key={rowIndex}
							rowIndex={rowIndex}
							cell={cell}
							showDiff={showDiff}
							diffProfile={diffProfile}
							isHovered={hoveredRowIndex === rowIndex}
							onRowHover={onRowHover}
						/>
					))}
				</article>
			) : (
				<div className="dual-lens__panel-empty text-danger small">{emptyMessage}</div>
			)}
		</div>
	);
}

function PanelMetaLine({ meta }) {
	const metaInfo = formatFetchMeta(meta);
	if (metaInfo?.error) {
		return (
			<p className="dual-lens__panel-meta dual-lens__panel-meta--error small mb-0">
				{metaInfo.error}
			</p>
		);
	}
	if (metaInfo?.label) {
		return <p className="dual-lens__panel-meta text-muted small mb-0">{metaInfo.label}</p>;
	}
	return null;
}

export default function DualLensCompareView({ content }) {
	const [showDiff, setShowDiff] = useState(true);
	const [comparisonMode, setComparisonMode] = useState(false);
	const [activeTab, setActiveTab] = useState("scroll");
	const [hoveredRowIndex, setHoveredRowIndex] = useState(null);

	const aiMd = useMemo(
		() => prepareMarkdownForBlocks(getMarkdown(content, "playwright_no_js")),
		[content]
	);
	const humanMd = useMemo(
		() => prepareMarkdownForBlocks(getMarkdown(content, "playwright_idle")),
		[content]
	);
	const scrollMd = useMemo(
		() => prepareMarkdownForBlocks(getMarkdown(content, "playwright_scroll")),
		[content]
	);

	const spineRows = useMemo(
		() => buildDualLensSpine(aiMd, humanMd, scrollMd, showDiff),
		[aiMd, humanMd, scrollMd, showDiff]
	);

	const humanAddedCount = useMemo(() => countHumanDiffRows(spineRows), [spineRows]);
	const scrollAddedCount = useMemo(() => countScrollAiDiffRows(spineRows), [spineRows]);
	const scrollOnlyCount = useMemo(() => countScrollIdleDiffRows(spineRows), [spineRows]);

	const aiColumnRows = useMemo(
		() => spineRows.map((row) => ({ rowIndex: row.rowIndex, cell: row.ai })),
		[spineRows]
	);
	const humanColumnRows = useMemo(
		() => spineRows.map((row) => ({ rowIndex: row.rowIndex, cell: row.human })),
		[spineRows]
	);
	const scrollColumnRows = useMemo(
		() => spineRows.map((row) => ({ rowIndex: row.rowIndex, cell: row.advanced })),
		[spineRows]
	);

	const scrollDeps = [spineRows.length, showDiff];
	const { aiRef, humanRef, advancedRef, syncAllFromSide } =
		useSyncedTripleBlockScroll(scrollDeps);

	useEffect(() => {
		requestAnimationFrame(() => syncAllFromSide("ai"));
	}, [activeTab, comparisonMode, syncAllFromSide]);

	useEffect(() => {
		if (!comparisonMode) {
			setActiveTab("scroll");
		}
	}, [comparisonMode]);

	const aiMeta = getFetchMeta(content?.fetch_meta, "playwright_no_js");
	const humanMeta = getFetchMeta(content?.fetch_meta, "playwright_idle");
	const scrollMeta = getFetchMeta(content?.fetch_meta, "playwright_scroll");

	const activeAddedCount = comparisonMode
		? activeTab === "human"
			? humanAddedCount
			: scrollOnlyCount
		: scrollAddedCount;
	const humanConfig = comparisonMode
		? activeTab === "human"
			? HUMAN_IDLE_CONFIG
			: HUMAN_SCROLL_CONFIG
		: HUMAN_SCROLL_CONFIG;
	const activeHumanMeta = comparisonMode
		? activeTab === "human"
			? humanMeta
			: scrollMeta
		: scrollMeta;
	const humanPanelTitle = comparisonMode ? "Human View" : "Human (with scroll)";
	const humanPanelIconClass = comparisonMode
		? "dual-lens__panel-icon--human"
		: "dual-lens__panel-icon--scroll";
	const humanPanelIcon = comparisonMode ? "fa-eye" : "fa-wand-magic-sparkles";
	const comparisonModeDescription = comparisonMode
		? "Full Dual Lens view with Human tabs (without interaction and with scroll)."
		: "Side-by-side AI View and Human View (with scroll). Highlights show changes vs AI View.";

	const placeholderCount = spineRows.filter(
		(r) => r.ai.type === "placeholder" || r.human.type === "placeholder"
	).length;

	const summaryParts = [];
	if (showDiff && humanAddedCount > 0) {
		summaryParts.push(
			`Human View adds ${humanAddedCount} section${humanAddedCount !== 1 ? "s" : ""} over AI View`
		);
	}
	if (showDiff && scrollAddedCount > 0) {
		summaryParts.push(
			`With scroll adds ${scrollAddedCount} section${scrollAddedCount !== 1 ? "s" : ""} over AI View`
		);
	}
	if (showDiff && scrollOnlyCount > 0) {
		summaryParts.push(
			`${scrollOnlyCount} section${scrollOnlyCount !== 1 ? "s" : ""} only appear after scroll`
		);
	}
	if (showDiff && placeholderCount > 0) {
		summaryParts.push(
			`${placeholderCount} aligned row${placeholderCount !== 1 ? "s" : ""} use placeholders`
		);
	}

	const simpleSummaryParts = [];
	if (showDiff && scrollAddedCount > 0) {
		simpleSummaryParts.push(
			`Human (with scroll) has ${scrollAddedCount} section${scrollAddedCount !== 1 ? "s" : ""} new or changed vs AI View`
		);
	}

	return (
		<div className="dual-lens__compare">
			{SHOW_COMPARISON_MODE && (
				<div className="dual-lens__comparison-mode-bar">
					<div className="dual-lens__comparison-mode-main">
						<h3 className="dual-lens__comparison-mode-title mb-1">Comparison Mode</h3>
						<p className="dual-lens__comparison-mode-description text-muted small mb-0">
							{comparisonModeDescription}
						</p>
					</div>
					<button
						type="button"
						className={`dual-lens__comparison-toggle${
							comparisonMode ? " dual-lens__comparison-toggle--on" : ""
						}`}
						role="switch"
						aria-checked={comparisonMode}
						onClick={() => setComparisonMode((v) => !v)}
					>
						<span className="dual-lens__comparison-toggle-track">
							<span className="dual-lens__comparison-toggle-thumb" />
							<span className="dual-lens__comparison-toggle-label">
								{comparisonMode ? "ON" : "OFF"}
							</span>
						</span>
					</button>
				</div>
			)}

			{comparisonMode ? (
			<div className="dual-lens__legend-bar">
				<div className="dual-lens__legend-main">
					<p className="dual-lens__legend-instructions mb-2">
						AI View on the left is the baseline. Each aligned row is one content section
						across all three captures - placeholders mark sections missing from AI or Human
						view. The badge counts rows with highlights, not individual highlighted lines.
						Hover a row to highlight the same section in every panel. Panels scroll
						together.
					</p>
					{showDiff && (
						<div className="dual-lens__legend-items">
							<span className="dual-lens__legend-item">
								<span className="dual-lens__legend-swatch dual-lens__legend-swatch--missing-ai" />
								Missing in AI View
							</span>
							<span className="dual-lens__legend-item">
								<span className="dual-lens__legend-swatch dual-lens__legend-swatch--missing-human" />
								Missing in Human View
							</span>
							<span className="dual-lens__legend-item">
								<span className="dual-lens__legend-swatch dual-lens__legend-swatch--js-delta" />
								New or changed vs AI View (Human tab)
							</span>
							<span className="dual-lens__legend-item">
								<span className="dual-lens__legend-swatch dual-lens__legend-swatch--scroll-only" />
								New or changed vs idle (scroll tab)
							</span>
						</div>
					)}
					{summaryParts.length > 0 && (
						<p className="dual-lens__legend-summary text-muted small mb-0">
							{summaryParts.join(" · ")}
						</p>
					)}
				</div>
				<Button
					type="button"
					color="light"
					size="sm"
					className="dual-lens__hide-highlights-btn border"
					onClick={() => setShowDiff((v) => !v)}
				>
					{showDiff ? "Hide highlights" : "Show highlights"}
				</Button>
			</div>
			) : (
			<div className="dual-lens__legend-bar dual-lens__legend-bar--simple">
				<div className="dual-lens__legend-main">
					<p className="dual-lens__legend-instructions mb-2">
						AI View on the left is the baseline. Hover a row to highlight the same
						section in both panels. Panels scroll together.
					</p>
					{showDiff && (
						<div className="dual-lens__legend-items">
							<span className="dual-lens__legend-item">
								<span className="dual-lens__legend-swatch dual-lens__legend-swatch--missing-ai" />
								Missing in AI View
							</span>
							<span className="dual-lens__legend-item">
								<span className="dual-lens__legend-swatch dual-lens__legend-swatch--js-delta" />
								New or changed vs AI View
							</span>
						</div>
					)}
					{simpleSummaryParts.length > 0 && (
						<p className="dual-lens__legend-summary text-muted small mb-0">
							{simpleSummaryParts.join(" · ")}
						</p>
					)}
				</div>
				<Button
					type="button"
					color="light"
					size="sm"
					className="dual-lens__hide-highlights-btn border"
					onClick={() => setShowDiff((v) => !v)}
				>
					{showDiff ? "Hide highlights" : "Show highlights"}
				</Button>
			</div>
			)}

			<div className="dual-lens__compare-grid">
				<div className="dual-lens__panel">
					<div className="dual-lens__panel-header">
						<div className="dual-lens__panel-heading">
							<div className="dual-lens__panel-title-row">
								<span
									className={`dual-lens__panel-icon ${AI_CONFIG.iconClass}`}
								>
									<i className={`fas ${AI_CONFIG.icon}`} />
								</span>
								<h3 className="dual-lens__panel-title mb-0">{AI_CONFIG.title}</h3>
								<div
									className="dual-lens__panel-header-spacer dual-lens__panel-header-spacer--toggle"
									aria-hidden="true"
								/>
							</div>
						</div>
						<div className="dual-lens__panel-subtitle-row">
							<span className="dual-lens__panel-subtitle">{AI_CONFIG.subtitle}</span>
						</div>
						<p className="dual-lens__panel-description text-muted small mb-2">
							{AI_CONFIG.description}
						</p>
						<PanelMetaLine meta={aiMeta} />
					</div>
					<SpineScrollPanel
						scrollRef={aiRef}
						columnRows={aiColumnRows}
						hoveredRowIndex={hoveredRowIndex}
						onRowHover={setHoveredRowIndex}
						showDiff={showDiff}
						diffProfile="idle"
						emptyMessage={
							aiMeta?.error ?? "No content could be extracted for AI View."
						}
					/>
				</div>

				<div className="dual-lens__panel dual-lens__panel--human">
					<div className="dual-lens__panel-header">
						<div className="dual-lens__panel-heading dual-lens__panel-heading--human">
							<div className="dual-lens__panel-title-row">
								<span className={`dual-lens__panel-icon ${humanPanelIconClass}`}>
									<i className={`fas ${humanPanelIcon}`} />
								</span>
								<h3 className="dual-lens__panel-title mb-0">{humanPanelTitle}</h3>
								{comparisonMode ? (
									<div
										className="dual-lens__human-toggle"
										role="group"
										aria-label="Human view mode"
									>
										<button
											type="button"
											className={`dual-lens__human-toggle-btn${
												activeTab === "human"
													? " dual-lens__human-toggle-btn--active"
													: ""
											}`}
											onClick={() => setActiveTab("human")}
										>
											<i
												className="fas fa-eye dual-lens__human-toggle-icon"
												aria-hidden="true"
											/>
											Without interaction
										</button>
										<button
											type="button"
											className={`dual-lens__human-toggle-btn${
												activeTab === "scroll"
													? " dual-lens__human-toggle-btn--active"
													: ""
											}`}
											onClick={() => setActiveTab("scroll")}
										>
											<i
												className="fas fa-hand-pointer dual-lens__human-toggle-icon"
												aria-hidden="true"
											/>
											With interaction (scroll)
										</button>
									</div>
								) : (
									<div
										className="dual-lens__panel-header-spacer dual-lens__panel-header-spacer--toggle"
										aria-hidden="true"
									/>
								)}
							</div>
						</div>
						<div className="dual-lens__panel-subtitle-row">
							<span className="dual-lens__panel-subtitle">{humanConfig.subtitle}</span>
							{showDiff && (
								<AddedBadge
									count={activeAddedCount}
									variant={
										comparisonMode && activeTab === "scroll" ? "scroll" : "ai"
									}
								/>
							)}
						</div>
						<p className="dual-lens__panel-description text-muted small mb-2">
							{humanConfig.description}
						</p>
						<PanelMetaLine meta={activeHumanMeta} />
					</div>
					<div className="dual-lens__panel-body-stack">
						<SpineScrollPanel
							scrollRef={humanRef}
							columnRows={humanColumnRows}
							hoveredRowIndex={hoveredRowIndex}
							onRowHover={setHoveredRowIndex}
							showDiff={showDiff}
							diffProfile="idle"
							stackedTab
							hidden={activeTab !== "human"}
							emptyMessage={
								humanMeta?.error ??
								"No content could be extracted for Human View."
							}
						/>
						<SpineScrollPanel
							scrollRef={advancedRef}
							columnRows={scrollColumnRows}
							hoveredRowIndex={hoveredRowIndex}
							onRowHover={setHoveredRowIndex}
							showDiff={showDiff}
							diffProfile={
								comparisonMode && activeTab === "scroll" ? "scroll" : "idle"
							}
							stackedTab
							hidden={activeTab !== "scroll"}
							emptyMessage={
								scrollMeta?.error ??
								"No content could be extracted for Human (with scroll) view."
							}
						/>
					</div>
				</div>
			</div>
		</div>
	);
}
